import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  MapPin,
  Phone,
  Star,
  Truck,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Building2,
  Home,
  Package,
  ShieldCheck,
  Clock
} from 'lucide-react';
import { SEO, BUSINESS_INFO } from '../SEO';
import { getLocationBySlug, LOCATIONS, isFullServiceLocation } from '../locations/locationData';
import { BookingSection } from '../BookingSection';
import { CitySeoContentSection, TRENDING_KEYWORD_STRING } from '../SeoContentSections';
import {
  getLocationLandingPath,
  getLocationSeoData,
  getNearbyLocationSlugs,
  resolveLocationSlug,
} from './locationSeoData';

const FULL_SERVICES = [
  {
    icon: Home,
    title: 'Residential Moving',
    desc: 'Houses, apartments, condos, and town homes — fully insured.',
    to: '/services/residential-moving'
  },
  {
    icon: Building2,
    title: 'Commercial Moving',
    desc: 'Office relocations, warehouse moves, and business transitions.',
    to: '/services/commercial-moving'
  },
  {
    icon: Sparkles,
    title: 'Residential Cleaning',
    desc: 'Standard cleans, deep cleans, and move-in/move-out service.',
    to: '/services/residential-cleaning'
  },
  {
    icon: Package,
    title: 'Moving Support',
    desc: 'Packing, loading help, unloading, and specialty item moves.',
    to: '/services/moving-support'
  }
];

const MOVING_ONLY_SERVICES = [
  {
    icon: Home,
    title: 'Residential Moving',
    desc: 'Houses, apartments, condos, and town homes — fully insured.',
    to: '/services/residential-moving'
  },
  {
    icon: Building2,
    title: 'Commercial Moving',
    desc: 'Office relocations, warehouse moves, and business transitions.',
    to: '/services/commercial-moving'
  },
  {
    icon: Package,
    title: 'Moving Support',
    desc: 'Packing, loading help, unloading, and specialty item moves.',
    to: '/services/moving-support'
  },
  {
    icon: Truck,
    title: 'Long-Distance Moves',
    desc: 'Licensed interstate authority — IN, KY, OH. Cross-state moves handled end-to-end.',
    to: '/services/residential-moving'
  }
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: 'Licensed & Insured' },
  { icon: Star, label: '5.0 Avg Rating' },
  { icon: Clock, label: 'On-Time Guarantee' },
  { icon: Truck, label: '1,000+ Moves Completed' }
];

const getLocationSchema = (loc, canonicalPath) => ({
  '@context': 'https://schema.org',
  '@type': 'MovingCompany',
  name: BUSINESS_INFO.name,
  image: BUSINESS_INFO.logo,
  url: BUSINESS_INFO.url,
  telephone: '+18126694165',
  email: BUSINESS_INFO.email,
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: BUSINESS_INFO.address.street,
    addressLocality: BUSINESS_INFO.address.city,
    addressRegion: BUSINESS_INFO.address.stateCode,
    postalCode: BUSINESS_INFO.address.zip,
    addressCountry: BUSINESS_INFO.address.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: Number(BUSINESS_INFO.geo.latitude),
    longitude: Number(BUSINESS_INFO.geo.longitude)
  },
  openingHoursSpecification: BUSINESS_INFO.hoursSpecification.map((spec) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: spec.dayOfWeek,
    opens: spec.opens,
    closes: spec.closes
  })),
  areaServed: {
    '@type': 'City',
    name: `${loc.name}, ${loc.stateAbbr}`
  },
  serviceType: ['Moving', 'House Cleaning', 'Deep Cleaning', 'Move-Out Cleaning', 'Office Cleaning'],
  availableLanguage: ['English'],
  sameAs: Object.values(BUSINESS_INFO.socialProfiles),
  mainEntityOfPage: `${BUSINESS_INFO.url}${canonicalPath}`,
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '500'
  }
});

const getBreadcrumbSchema = (loc, canonicalPath) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: BUSINESS_INFO.url },
    { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${BUSINESS_INFO.url}/service-areas` },
    { '@type': 'ListItem', position: 3, name: loc.name, item: `${BUSINESS_INFO.url}${canonicalPath}` }
  ]
});

const getFaqSchema = (loc) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (loc.faq || []).map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a }
  }))
});

export const LocationPage = () => {
  const { city, cityLanding, slug } = useParams();
  const locationRouteValue = cityLanding || slug;
  const resolvedSlug = city || resolveLocationSlug(locationRouteValue);
  const loc = getLocationBySlug(resolvedSlug);

  if (!loc) return <Navigate to="/service-areas" replace />;

  const isFullService = isFullServiceLocation(loc.slug);
  const servicesToShow = isFullService ? FULL_SERVICES : MOVING_ONLY_SERVICES;
  const seoData = getLocationSeoData(loc.slug);
  const canonicalPath = getLocationLandingPath(loc.slug);
  const nearbyLocations = getNearbyLocationSlugs(loc.slug)
    .map((slug) => getLocationBySlug(slug))
    .filter(Boolean);
  const otherLocations = nearbyLocations.length > 0
    ? nearbyLocations
    : LOCATIONS.filter((l) => l.slug !== loc.slug).slice(0, 3);

  const heroHeading = isFullService
    ? `Professional Movers & Cleaners in ${loc.name}, ${loc.stateAbbr}`
    : `Professional Movers in ${loc.name}, ${loc.stateAbbr}`;

  const seoTitle = seoData?.title || (isFullService
    ? `Professional Movers & Cleaners in ${loc.name}, ${loc.stateAbbr}`
    : `Professional Movers in ${loc.name}, ${loc.stateAbbr}`);

  const seoDescription = seoData?.description || (isFullService
    ? `Licensed moving and cleaning services in ${loc.name}, ${loc.state}. Residential and commercial. Free quotes, flat-rate pricing, 5-star reviews. Call (812) 669-4165.`
    : `Licensed, insured professional movers in ${loc.name}, ${loc.state}. Residential, commercial, and long-distance moves. Free quotes, flat-rate pricing, 5-star reviews. Call (812) 669-4165.`);

  const seoKeywords = isFullService
    ? `movers ${loc.name} ${loc.stateAbbr}, moving company ${loc.name} ${loc.state}, cleaning services ${loc.name}, ${loc.name} house cleaning, ${loc.name} moving company, residential movers ${loc.name}, ${TRENDING_KEYWORD_STRING}`
    : `movers ${loc.name} ${loc.stateAbbr}, moving company ${loc.name} ${loc.state}, ${loc.name} movers near me, residential movers ${loc.name}, commercial movers ${loc.name}, long distance movers ${loc.name}, ${TRENDING_KEYWORD_STRING}`;

  const introHeading = isFullService
    ? `Local Movers and Cleaners Who Know ${loc.name}`
    : `Local Movers Who Know ${loc.name}`;

  const highlightsHeading = isFullService
    ? `Why ${loc.name} Residents Choose Swift Move and Clean`
    : `Why ${loc.name} Residents Choose Swift Move and Clean for Moving`;

  const finalCtaHeading = isFullService
    ? `Ready to Move or Clean in ${loc.name}?`
    : `Ready to Move in ${loc.name}?`;

  const servicesHeading = isFullService
    ? `Our Services in ${loc.name}`
    : `Our Moving Services in ${loc.name}`;

  return (
    <div className="min-h-screen bg-white pt-20" data-testid={`location-page-${loc.slug}`}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={canonicalPath}
        schema={[getLocationSchema(loc, canonicalPath), getBreadcrumbSchema(loc, canonicalPath), getFaqSchema(loc)]}
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden text-white py-20"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #0891b2 100%)'
        }}
      >
        <img
          src="/images/hero-sunset-move.webp"
          alt=""
          aria-hidden="true"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="container relative mx-auto px-4">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full mb-5 backdrop-blur-sm">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">
                Serving {loc.name}, {loc.stateAbbr}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
              {heroHeading}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed mb-8 max-w-3xl">
              {isFullService
                ? `Licensed, insured, and consistently 5-star rated. Whether you're moving across town or relocating to ${loc.name} from another state, our local crews handle every detail with the care of neighbors — because most of us are.`
                : `Licensed, insured, and consistently 5-star rated. Whether you're moving within ${loc.name} or relocating across state lines, our Columbus-based crews bring the same white-glove care to every job — big or small, local or long-distance.`}
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              <Button
                size="lg"
                className="bg-bright-orange hover:bg-orange-600 text-white shadow-lg"
                onClick={() => (window.location.href = 'tel:8126694165')}
                data-testid={`loc-hero-call-${loc.slug}`}
              >
                <Phone className="h-5 w-5 mr-2" />
                (812) 669-4165
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-navy-blue"
                onClick={() => (window.location.href = '#booking')}
                data-testid={`loc-hero-quote-${loc.slug}`}
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-white/90">
              {TRUST_BADGES.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {b.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Facts Strip */}
      <section className="bg-white shadow-lg -mt-8 relative z-10 mx-4 rounded-xl max-w-5xl lg:mx-auto mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x">
          <div className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Service Area</p>
            <p className="font-semibold text-gray-900">
              {loc.name}, {loc.state}
            </p>
            <p className="text-sm text-gray-600 mt-1">Population {loc.population}</p>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">From Our HQ</p>
            <p className="font-semibold text-gray-900">{loc.distanceFromHQ}</p>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Zip Codes Served</p>
            <p className="font-semibold text-gray-900">{loc.zipCodes.join(', ')}</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="container mx-auto px-4 pb-12">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-5 leading-tight">
            {introHeading}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg">{loc.intro}</p>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-light-gray py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {highlightsHeading}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {loc.highlights.map((h, i) => (
                <Card
                  key={i}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`loc-highlight-${i + 1}`}
                >
                  <CardContent className="p-5 flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-blue mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">{h}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {servicesHeading}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {isFullService
              ? `Every service below is available in ${loc.name} with flat-rate, transparent pricing.`
              : `Professional moving services for ${loc.name} residents and businesses, with flat-rate, transparent pricing. For recurring cleaning plans, our coverage area centers on communities within 30 minutes of Columbus — ask us if ${loc.name} qualifies.`}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesToShow.map((s) => {
            const Icon = s.icon;
            return (
              <Link key={s.title} to={s.to} className="group">
                <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all group-hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="h-12 w-12 rounded-lg bg-sky-50 flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-sky-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-sky-blue transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Neighborhoods + Landmarks */}
      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-sky-blue" />
                  Neighborhoods We Serve
                </h3>
                <ul className="space-y-2">
                  {loc.neighborhoods.map((n) => (
                    <li key={n} className="text-gray-700 flex items-center gap-2">
                      <ArrowRight className="h-3 w-3 text-sky-blue flex-shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-bright-orange" />
                  Local Landmarks & Routes
                </h3>
                <ul className="space-y-2 mb-5">
                  {loc.landmarks.map((l) => (
                    <li key={l} className="text-gray-700 flex items-center gap-2">
                      <ArrowRight className="h-3 w-3 text-bright-orange flex-shrink-0" />
                      {l}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 leading-relaxed italic border-t pt-4">
                  {loc.localNote}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <CitySeoContentSection loc={loc} isFullService={isFullService} />

      {/* Testimonial */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-8 md:p-10">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-5 italic">
                "{loc.testimonial.text}"
              </p>
              <div className="flex items-center gap-3 pt-4 border-t">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-blue to-navy-blue text-white flex items-center justify-center font-bold">
                  {loc.testimonial.name[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{loc.testimonial.name}</p>
                  <p className="text-sm text-gray-500">{loc.testimonial.neighborhood}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Booking */}
      <BookingSection />

      {/* FAQ */}
      {loc.faq && loc.faq.length > 0 && (
        <section className="bg-light-gray py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions — {loc.name}
              </h2>
              <div className="space-y-4">
                {loc.faq.map((f, idx) => (
                  <Card
                    key={idx}
                    className="border-0 shadow-sm"
                    data-testid={`loc-faq-${idx + 1}`}
                  >
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-gray-900 mb-2">{f.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{f.a}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Locations */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          Nearby Cities We Serve
        </h2>
        <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
          {otherLocations.map((o) => (
            <Link key={o.slug} to={getLocationLandingPath(o.slug)}>
              <Badge
                variant="outline"
                className="text-sm px-4 py-2 hover:bg-sky-blue hover:text-white hover:border-sky-blue cursor-pointer transition-colors"
              >
                {o.name}, {o.stateAbbr}
              </Badge>
            </Link>
          ))}
          <Link to="/service-areas">
            <Badge className="text-sm px-4 py-2 bg-navy-blue text-white cursor-pointer hover:bg-navy-blue/90">
              View All Service Areas →
            </Badge>
          </Link>
        </div>
        <div className="max-w-3xl mx-auto text-center text-gray-700 mt-8 space-y-4">
          <p>
            Swift Move and Clean serves all of central Indiana.{' '}
            <Link to="/service-areas" className="font-semibold text-sky-blue hover:underline">
              View all 39+ service areas →
            </Link>
          </p>
          {otherLocations.length > 0 && (
            <p>
              Nearby cities we serve:{' '}
              {otherLocations.map((location, index) => (
                <React.Fragment key={location.slug}>
                  <Link to={getLocationLandingPath(location.slug)} className="font-semibold text-sky-blue hover:underline">
                    {location.name}
                  </Link>
                  {index === otherLocations.length - 1 ? '' : ' | '}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="text-white py-16"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 60%, #0891b2 100%)'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {finalCtaHeading}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote in under 2 hours. No pressure, no hidden fees.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              className="bg-bright-orange hover:bg-orange-600 text-white shadow-lg"
              onClick={() => (window.location.href = 'tel:8126694165')}
              data-testid={`loc-cta-call-${loc.slug}`}
            >
              <Phone className="h-5 w-5 mr-2" />
              Call (812) 669-4165
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-navy-blue"
              onClick={() => (window.location.href = '#booking')}
              data-testid={`loc-cta-quote-${loc.slug}`}
            >
              Request a Quote
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
